/**
 * Filter concern for DataViewTable - generic config filters, the
 * filter toggle, summary-chip filtering and fpCode validation.
 *
 * State is lifted-first: when the route supplies `filterValues` /
 * `filterEnabled` / `summaryChipsControl` (DynamicExceptionRoute via
 * `useExceptionFilterValues`), the hook reads those; otherwise it falls
 * back to internal state seeded from `config.filters` (platform reports,
 * standalone Tools tabs). Server-side filters (daterange, server-driven
 * search) are skipped in the client predicates - the parent applies them
 * via its API params.
 *
 * The "reset to page 0 on filter change" rule stays in the component
 * (it spans the filtering + pagination concerns), so the setters here
 * write state only. Extracted as a pure structural move - state, memo
 * dependency arrays and predicate branches are byte-identical to the
 * inline originals.
 */
import { useEffect, useMemo, useState } from 'react'
import type {
  ExceptionConfig,
  ExceptionContext,
  ExceptionFilterDef,
} from '../../../config/exceptions/types'
// Filter-value initialisation lives in `exceptionFilterControls` so the
// route hook (`useExceptionFilterValues`) and this table can share one
// definition — keep them in lock-step.
import { buildInitialFilterValues } from '../../inspector/shared/exceptionFilterControls'

/**
 * Server-driven search: when set, the search input in the toolbar is
 * controlled by the parent (which forwards the value to its API call)
 * instead of running a client-side `predicate` on the loaded page.
 * Only the `'search'` filter in `config.filters` is delegated — other
 * filter types still run client-side. Pairs with ServerPaginationConfig
 * because client-side search is meaningless once pagination is server-
 * driven (the input only sees the current page of N rows).
 */
export interface ServerSearchConfig {
  value: string
  onChange: (value: string) => void
}

/**
 * External control for `config.summaryChips` state. Lets the parent
 * (route) lift the active-chip key so the chips can render inside
 * `DataViewFilterPanel` while filtering still happens here.
 */
export interface SummaryChipsControl {
  activeKey: string | null
  setActiveKey: (key: string | null) => void
  /**
   * Multi-select state (used only when `config.summaryChips.multiSelect`
   * is true). The single-key fields above stay defined as a no-op for
   * back-compat - the table reads from this set instead when multi-select
   * is on.
   */
  activeKeys?: string[]
  setActiveKeys?: (keys: string[]) => void
}

/**
 * Validate rows match expected fpCode (inspector defence-in-depth)
 */
const filterByFpCode = (
  data: Record<string, any>[],
  fpCode?: string
): Record<string, any>[] => {
  if (!fpCode) return data
  return data.filter(row => {
    if (row.fp_code === undefined) return true
    if (row.fp_code === fpCode) return true
    console.warn(`DataViewTable: filtered row with fp_code="${row.fp_code}" (expected "${fpCode}")`)
    return false
  })
}

export function useTableFiltering({
  config,
  data,
  variant,
  fpCode,
  response,
  externalFilterValues,
  externalUpdateFilterValue,
  externalFilterEnabled,
  summaryChipsControl,
  serverSearch,
  isServerPaginated,
}: {
  config: ExceptionConfig
  data: Record<string, any>[]
  variant: ExceptionContext
  fpCode?: string
  response?: Record<string, any>
  externalFilterValues?: Record<string, any>
  externalUpdateFilterValue?: (key: string, value: any) => void
  externalFilterEnabled?: boolean
  summaryChipsControl?: SummaryChipsControl
  serverSearch?: ServerSearchConfig
  isServerPaginated: boolean
}) {
  const [internalFilterEnabled, setInternalFilterEnabled] = useState(
    config.filterToggle?.defaultEnabled ?? false,
  )
  const filterEnabled =
    externalFilterEnabled !== undefined ? externalFilterEnabled : internalFilterEnabled
  // Internal fallback state when no external control is provided (e.g.
  // platform reports). When the route lifts state via summaryChipsControl
  // (e.g. category-alignment exception rendered through the panel) we
  // defer to that instead so the panel chips + table filtering stay in
  // sync.
  const [internalActiveChipKey, setInternalActiveChipKey] = useState<string | null>(null)
  const activeChipKey = summaryChipsControl
    ? summaryChipsControl.activeKey
    : internalActiveChipKey
  const setActiveChipKey = summaryChipsControl
    ? summaryChipsControl.setActiveKey
    : setInternalActiveChipKey
  // Multi-select chip state. Used only when ``config.summaryChips.multiSelect``
  // is true. Defaults to the configured ``defaultActiveKeys`` so the table
  // can land on an actionable subset (e.g. Missing+Partial) on first load.
  const chipMultiSelect = !!config.summaryChips?.multiSelect
  const [internalActiveChipKeys, setInternalActiveChipKeys] = useState<string[]>(
    () => [...(config.summaryChips?.defaultActiveKeys ?? [])],
  )
  const activeChipKeys: string[] = chipMultiSelect
    ? summaryChipsControl?.activeKeys ?? internalActiveChipKeys
    : []
  const setActiveChipKeys = (keys: string[]) => {
    if (summaryChipsControl?.setActiveKeys) {
      summaryChipsControl.setActiveKeys(keys)
    } else {
      setInternalActiveChipKeys(keys)
    }
  }
  const [internalFilterValues, setInternalFilterValues] = useState<Record<string, any>>(
    () => buildInitialFilterValues(config.filters),
  )
  const filterValues =
    externalFilterValues !== undefined ? externalFilterValues : internalFilterValues

  const hasFilter = !!config.filterToggle

  useEffect(() => {
    // Internal fallback path — when the parent route lifts filter state
    // it owns the reset (the route hook reacts to `config.filters` too).
    // The matching client-page reset lives in `useTablePagination` with
    // the same dependency array, so the two halves fire in lock-step.
    if (externalFilterValues === undefined) {
      setInternalFilterValues(buildInitialFilterValues(config.filters))
    }
  }, [config.filters, data, isServerPaginated, externalFilterValues])

  const chipCounts = config.summaryChips && response
    ? (response[config.summaryChips.countsKey] as Record<string, number> | undefined)
    : undefined
  const hasSummaryChips = !!config.summaryChips && !!chipCounts

  const validatedData = useMemo(
    () => variant === 'inspector' ? filterByFpCode(data, fpCode) : data,
    [data, variant, fpCode]
  )

  /**
   * Resolve dynamic-option filters against the current row set so options
   * like the category dropdown reflect what's actually present in the data.
   */
  const effectiveFilters = useMemo<ExceptionFilterDef[]>(() => {
    return (config.filters ?? []).map(f => {
      if (f.type === 'multiselect' && f.extractOptions) {
        return { ...f, options: f.extractOptions(validatedData) }
      }
      return f
    })
  }, [config.filters, validatedData])

  const hasGenericFilters = effectiveFilters.length > 0

  const filteredByConfig = useMemo(() => {
    if (!hasGenericFilters) return validatedData
    return validatedData.filter(row => {
      for (const f of effectiveFilters) {
        const value = filterValues[f.key]
        if (f.type === 'multiselect') {
          const selected = (value ?? []) as string[]
          if (selected.length === 0) continue
          const rowValue = f.getValue ? f.getValue(row) : row[f.key]
          if (rowValue == null || !selected.includes(String(rowValue))) return false
        } else if (f.type === 'select') {
          const v = (value ?? f.defaultValue ?? 'all') as string
          if (v === 'all') continue
          // No predicate -> server-side filter (applied via buildFetchParams).
          if (!f.predicate) continue
          if (!f.predicate(row, v)) return false
        } else if (f.type === 'toggle') {
          // No predicate -> server-side filter (applied via buildFetchParams).
          if (!f.predicate) continue
          if (value && !f.predicate(row)) return false
        } else if (f.type === 'daterange') {
          // Daterange is always server-side via buildFetchParams.
          continue
        } else if (f.type === 'search') {
          // Server-driven search: parent applies the filter via the API,
          // so the loaded page is already narrowed. Running the local
          // predicate here would either no-op (if input mirrors URL) or
          // double-filter and hide matching rows.
          if (serverSearch) continue
          if (!f.predicate) continue
          const term = ((value ?? '') as string).trim()
          if (!term) continue
          if (!f.predicate(row, term)) return false
        }
      }
      return true
    })
  }, [
    validatedData,
    effectiveFilters,
    filterValues,
    hasGenericFilters,
    serverSearch,
  ])

  const toggleFiltered = useMemo(() => {
    if (!hasFilter || !filterEnabled || !config.filterToggle) return filteredByConfig
    return filteredByConfig.filter(config.filterToggle.predicate)
  }, [filteredByConfig, hasFilter, filterEnabled, config.filterToggle])

  const filteredData = useMemo(() => {
    if (!config.summaryChips) return toggleFiltered
    if (chipMultiSelect) {
      if (activeChipKeys.length === 0) return toggleFiltered
      const activeSet = new Set(activeChipKeys)
      const categoriesByKey = new Map(
        config.summaryChips.categories.map((c) => [c.key, c]),
      )
      const field = config.summaryChips.statusField
      return toggleFiltered.filter((row) => {
        for (const key of activeSet) {
          const category = categoriesByKey.get(key)
          if (category?.predicate) {
            if (category.predicate(row)) return true
          } else if (row[field] === key) {
            return true
          }
        }
        return false
      })
    }
    if (!activeChipKey) return toggleFiltered
    const category = config.summaryChips.categories.find(c => c.key === activeChipKey)
    if (category?.predicate) {
      return toggleFiltered.filter(category.predicate)
    }
    const field = config.summaryChips.statusField
    return toggleFiltered.filter(row => row[field] === activeChipKey)
  }, [toggleFiltered, config.summaryChips, activeChipKey, activeChipKeys, chipMultiSelect])

  const isChipActive = (key: string): boolean =>
    chipMultiSelect ? activeChipKeys.includes(key) : activeChipKey === key

  // Either the route lifted the setter (preferred) or we fall back to a
  // local writer for standalone use (eg. platform reports rendering the
  // table without the inspector panel). The page-0 reset rides in the
  // component's `updateFilterValue` wrapper (cross-concern glue).
  const setFilterValue = (key: string, value: any) => {
    if (externalUpdateFilterValue) {
      externalUpdateFilterValue(key, value)
    } else {
      setInternalFilterValues((prev) => ({ ...prev, [key]: value }))
    }
  }

  return {
    filteredData,
    filterValues,
    setFilterValue,
    filterEnabled,
    setInternalFilterEnabled,
    hasFilter,
    effectiveFilters,
    chipMultiSelect,
    activeChipKey,
    setActiveChipKey,
    activeChipKeys,
    setActiveChipKeys,
    isChipActive,
    chipCounts,
    hasSummaryChips,
  }
}
